// Lightweight, provider-agnostic analytics. Fires events into whatever is
// connected on the page (Google Tag Manager dataLayer, GA4 gtag, Meta Pixel
// fbq). If nothing is connected yet, events are queued on window.dataLayer and
// are picked up automatically once a tag manager loads. Never throws — analytics
// must never break the page or block a conversion.

type EventParams = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  }
}

// Google Analytics 4 measurement ID. Set VITE_GA_MEASUREMENT_ID in the
// environment, or paste the "G-..." id here as the fallback string.
const GA_MEASUREMENT_ID: string =
  (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined) ||
  "G-0JDR45B506";

/** True once a GA measurement id is configured — gates the consent banner. */
export const analyticsEnabled = Boolean(GA_MEASUREMENT_ID);

// Meta (Facebook) Pixel ID. Loaded only after cookie consent, same as GA.
const META_PIXEL_ID = "2510678659433038";

const CONSENT_KEY = "refound-analytics-consent";

export type AnalyticsConsent = "granted" | "denied" | null;

/** The visitor's stored cookie-consent choice, or null if not answered yet. */
export function getAnalyticsConsent(): AnalyticsConsent {
  if (typeof window === "undefined") return null;
  try {
    const value = window.localStorage.getItem(CONSENT_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    return null;
  }
}

/**
 * Persist the visitor's choice. On "granted", GA loads immediately and the
 * current page is reported (AnalyticsTracker's effect already ran by then).
 */
export function setAnalyticsConsent(consent: "granted" | "denied"): void {
  try {
    window.localStorage.setItem(CONSENT_KEY, consent);
  } catch {
    /* swallow — analytics is best-effort */
  }
  if (consent === "granted") {
    initGoogleAnalytics();
    initMetaPixel();
    trackPageView(window.location.pathname + window.location.search);
  }
}

/**
 * Load GA4 (gtag.js) and configure it. Call once from the client entry.
 * No-op during SSR, when no measurement id is set, or if already loaded.
 * `send_page_view: false` because the SPA router fires page_view itself
 * (see AnalyticsTracker) — otherwise the first page would be counted twice.
 */
export function initGoogleAnalytics(): void {
  if (typeof window === "undefined" || !GA_MEASUREMENT_ID) return;
  if (typeof window.gtag === "function") return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // gtag.js requires the literal `arguments` object; a rest-array is ignored.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments as unknown as Record<string, unknown>);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

/**
 * Load the Meta Pixel (fbevents.js) and fire the initial PageView.
 * Loi 25 / GDPR: only called after the visitor grants cookie consent —
 * same gate as Google Analytics. No-op during SSR or if already loaded.
 * This is the official Meta snippet translated to TypeScript: fbq is a
 * stub that queues calls until the async script takes over.
 */
export function initMetaPixel(): void {
  if (typeof window === "undefined" || !META_PIXEL_ID) return;
  if (typeof window.fbq === "function") return;

  type FbqStub = ((...args: unknown[]) => void) & {
    callMethod?: (...args: unknown[]) => void;
    queue: unknown[];
    push: unknown;
    loaded: boolean;
    version: string;
  };
  const fbq: FbqStub = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  } as FbqStub;
  fbq.queue = [];
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  window.fbq = fbq;
  (window as unknown as Record<string, unknown>)._fbq =
    (window as unknown as Record<string, unknown>)._fbq || fbq;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);

  window.fbq("init", META_PIXEL_ID);
  window.fbq("track", "PageView");
}

/** Report an SPA page view (initial load and every route change). */
export function trackPageView(path: string): void {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  try {
    window.gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });
  } catch {
    /* swallow — analytics is best-effort */
  }
}

/** Push a named event to every analytics sink available on the page. */
export function trackEvent(event: string, params: EventParams = {}): void {
  if (typeof window === "undefined") return;
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...params });
    if (typeof window.gtag === "function") window.gtag("event", event, params);
    if (typeof window.fbq === "function") window.fbq("trackCustom", event, params);
  } catch {
    /* swallow — analytics is best-effort */
  }
}

export type StoreName = "app_store" | "google_play";

/**
 * Conversion event for a store download button. `location` distinguishes which
 * button was clicked (e.g. "hero", "second_cta", "nav") so you can see which
 * part of the page drives downloads. `store` tells Apple App Store clicks apart
 * from Google Play clicks (event names: `app_store_click` / `play_store_click`).
 */
export function trackStoreClick(
  store: StoreName,
  location: string,
  language: string,
): void {
  trackEvent(store === "google_play" ? "play_store_click" : "app_store_click", {
    location,
    language,
    destination: store,
  });

  // Also fire a Meta *standard* event so the click can be used as a conversion
  // to optimize ad delivery in Meta Ads Manager (custom events alone can't).
  // A download-button click maps naturally to a "Lead".
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    try {
      window.fbq("track", "Lead", {
        content_name: store === "google_play" ? "google_play_download" : "app_store_download",
        location,
        language,
      });
    } catch {
      /* swallow — analytics is best-effort */
    }
  }
}

/** Backwards-compatible alias for the Apple App Store button. */
export function trackAppStoreClick(location: string, language: string): void {
  trackStoreClick("app_store", location, language);
}
